import { useEffect, useState } from 'react';
import { ChatParticipant } from '@my-ipractice/types';
import { Button } from '../../../ui/button';
import { Files, UserRoundCog } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { SmallCompanyLogo } from '@/assets/svgAsComponents/small-company-logo';
import { SearchMessages } from './search-messages';
import { ChatParticipantsDialog } from '../chat-participants/chat-participants-dialog';
import { SharedFilesDialog } from './shared-files-dialog';
import { VideoCallButton } from '@/components/video-call/video-call-button';
import { useChatContext } from 'stream-chat-react';
import { useTranslation } from 'react-i18next';

export const ChatHeader = () => {
  const { channel } = useChatContext();

  const [isChatParticipantsDialogOpen, setIsChatParticipantsDialogOpen] = useState(false);
  const [isSharedFilesDialogOpen, setIsSharedFilesDialogOpen] = useState(false);

  const [memberFirstNames, setMemberFirstNames] = useState<string[]>([]);
  const [fullListOfMembers, setFullListOfMembers] = useState<ChatParticipant[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    if (channel == null) return;

    channel.queryMembers({}, undefined, {}).then((res) => {
      const allMembers = res.members
        .filter((m) => m.user?.name != null)
        .map((m) => {
          return {
            name: m.user!.name!,
            role: m.user!.role!,
            image: m.user?.image as string | undefined,
          };
        });

      setFullListOfMembers(
        allMembers.sort((a, b) => {
          if (a.role === 'client') return -1;
          if (b.role === 'client') return 1;

          if (a.name.startsWith('iPractice')) return 1;
          if (b.name.startsWith('iPractice')) return -11;

          return a.name.localeCompare(b.name);
        })
      );

      const firstNames = res.members
        .filter(
          (m) =>
            m.user?.name != null && // should not be necessary, but apparently nullable
            !m.user!.name!.startsWith('iPractice') && // exclude iPractice chatbot
            m.user!.role != 'client' // exclude client him/herself
        )
        .map((m) => m.user!.name!.split(' ')[0]);

      firstNames.sort((a, b) => a.localeCompare(b));

      setMemberFirstNames(firstNames);
    });
  }, [channel]);

  return (
    <div className="py-5 px-4 lg:px-12 border-b flex items-center justify-between bg-background w-full h-fit lg:h-[80px]">
      <div className="w-full flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div className="w-full lg:w-2/3 flex flex-col lg:flex-row items-start lg:items-center space-x-0 lg:space-x-2 space-y-2 lg:space-y-0">
          <div className="flex items-center space-x-2 text-foreground">
            <div className="min-w-7 min-h-7 w-7 h-7">
              <SmallCompanyLogo />
            </div>
            <h1
              className="font-bold text-lg xl:text-2xl inline-block cursor-pointer hover:underline whitespace-nowrap"
              onClick={() => setIsChatParticipantsDialogOpen(true)}
              data-testid="chat-header-title"
            >
              {memberFirstNames.length > 0
                ? t('chat_page.titleWithMembers', { members: memberFirstNames.join(', ') })
                : t('chat_page.titleWithoutMembers')}
            </h1>
          </div>
          <SearchMessages />
        </div>

        <div className="w-full lg:w-fit flex items-center space-x-2">
          <VideoCallButton />
          <Button
            variant="outline"
            className="gap-2 w-1/2 lg:w-auto"
            onClick={() => setIsSharedFilesDialogOpen(true)}
            data-testid="documents-button"
          >
            <div className="w-4 h-4">
              <Files className="w-4 h-4" />
            </div>
            {t('chat_page.documentsButton')}
          </Button>

          <Link to="/auth/settings" className="w-1/2 lg:w-auto">
            <Button variant="outline" className="gap-2 w-full" data-testid="settings-button">
              <div className="w-4 h-4">
                <UserRoundCog className="w-4 h-4" />
              </div>
              {t('chat_page.settingsButton')}
            </Button>
          </Link>
        </div>
      </div>

      <ChatParticipantsDialog
        participants={fullListOfMembers}
        open={isChatParticipantsDialogOpen}
        setOpen={setIsChatParticipantsDialogOpen}
      />

      <SharedFilesDialog open={isSharedFilesDialogOpen} setOpen={setIsSharedFilesDialogOpen} />
    </div>
  );
};
